import { useEffect, useState } from 'react'
import { Monitor, Smartphone, Laptop, Globe, Trash2, LogOut, HardDrive } from 'lucide-react'
import { authApi, type UserSessionView, type PushDeviceView, type MyProbeDevice } from '../../services/api'
import { dialog } from '../../services/dialog'

function clientIcon(clientType: string) {
  if (clientType === 'mobile') return <Smartphone size={16} />
  if (clientType === 'desktop') return <Laptop size={16} />
  if (clientType === 'web') return <Monitor size={16} />
  return <Globe size={16} />
}

export function SessionsSection() {
  const [sessions, setSessions] = useState<UserSessionView[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [pushDevices, setPushDevices] = useState<PushDeviceView[]>([])
  const [probes, setProbes] = useState<MyProbeDevice[]>([])

  async function load() {
    setLoading(true)
    try {
      const res = await authApi.sessions.list()
      setSessions(res.data.sessions)
    } finally {
      setLoading(false)
    }
  }

  async function loadExtras() {
    try {
      const [pd, pr] = await Promise.all([authApi.sessions.pushDevices(), authApi.sessions.probes()])
      setPushDevices(pd.data.devices)
      setProbes(pr.data.devices.filter((d) => d.isMine))
    } catch { /* push/probe modules may be unavailable; show nothing */ }
  }

  useEffect(() => { void load(); void loadExtras() }, [])

  // Only non-current sessions are revoked here (the Revoke button is gated to
  // `!s.current`). Logging out the device you're on is handled by the app's
  // normal logout, which is server-aware. Revoking another device is low-risk
  // and reversible (they just sign in again), so no confirmation prompt.
  async function revokeOne(s: UserSessionView) {
    setBusy(s.id)
    try {
      await authApi.sessions.revoke(s.id)
      await load()
    } finally {
      setBusy(null)
    }
  }

  async function revokeOthers() {
    const ok = await dialog.confirm({
      title: 'Log out everywhere else?',
      message: 'All other sessions will be signed out. This device stays logged in.',
      tone: 'destructive',
      confirmLabel: 'Log out others',
    })
    if (!ok) return
    setBusy('others')
    try {
      await authApi.sessions.revokeOthers()
      await load()
    } finally {
      setBusy(null)
    }
  }

  async function removePush(d: PushDeviceView) {
    const ok = await dialog.confirm({ title: 'Remove device?', message: `Stop sending push notifications to ${d.deviceName || 'this device'}?`, tone: 'destructive', confirmLabel: 'Remove' })
    if (!ok) return
    await authApi.sessions.removePushDevice(d.id)
    await loadExtras()
  }

  async function unpair(d: MyProbeDevice) {
    const ok = await dialog.confirm({ title: 'Unpair agent?', message: `Disconnect ${d.name} from your account?`, tone: 'destructive', confirmLabel: 'Unpair' })
    if (!ok) return
    await authApi.sessions.unpairProbe(d.id)
    await loadExtras()
  }

  const others = sessions.filter((s) => !s.current && !s.revokedAt)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Active sessions</h2>
          <p className="text-xs text-muted-foreground">Devices and apps currently signed in to your account.</p>
        </div>
        {others.length > 0 && (
          <button
            onClick={revokeOthers}
            disabled={busy === 'others'}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-destructive hover:bg-muted disabled:opacity-50 transition duration-150"
          >
            <LogOut size={13} /> Log out everywhere else
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="text-xs text-muted-foreground">No active sessions.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {sessions.map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-3 py-2.5">
              <span className="text-muted-foreground">{clientIcon(s.clientType)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm text-foreground">{s.label || 'Unknown device'}</span>
                  {s.current && (
                    <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">This device</span>
                  )}
                  {s.revokedAt && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">Signed out</span>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {s.ip || 'unknown IP'} · last active {new Date(s.lastSeenAt).toLocaleString()}
                </p>
              </div>
              {!s.revokedAt && !s.current && (
                <button
                  onClick={() => revokeOne(s)}
                  disabled={busy === s.id}
                  className="inline-flex items-center gap-1 text-xs text-destructive hover:underline disabled:opacity-50"
                >
                  <Trash2 size={13} /> Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pushDevices.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Mobile devices</h2>
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {pushDevices.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-muted-foreground"><Smartphone size={16} /></span>
                <div className="min-w-0 flex-1">
                  <span className="truncate text-sm text-foreground">{d.deviceName || d.platform}</span>
                  <p className="truncate text-xs text-muted-foreground">{d.platform} · last active {new Date(d.lastSeenAt).toLocaleString()}</p>
                </div>
                <button onClick={() => removePush(d)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline">
                  <Trash2 size={13} /> Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {probes.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Probe agents</h2>
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {probes.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-muted-foreground"><HardDrive size={16} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm text-foreground">{d.name}</span>
                    {d.online && <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">Online</span>}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{d.os || 'unknown OS'}{d.lastSeenAt ? ` · last seen ${new Date(d.lastSeenAt).toLocaleString()}` : ''}</p>
                </div>
                <button onClick={() => unpair(d)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline">
                  <Trash2 size={13} /> Unpair
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
